/**
 * Sankey chart builder (ADR-0079 §4.1, §4.4).
 *
 * Each row → one link `(source, target, value)`. The renderer collects all
 * unique source AND target names across rows into the node list (deduped).
 * Multiple rows with the same `(source, target)` pair are summed.
 */

import type { SankeySpec } from "../echartsSpec.js";
import {
  buildNoDataGraphic,
  buildTooltipFormatter,
  resolveColumnIndex,
  resolvePalette,
} from "./shared.js";

type EChartsOption = Record<string, unknown>;

export function buildSankeyOption(
  spec: SankeySpec,
  rows: unknown[][],
  dimensions: string[],
): EChartsOption {
  const isEmpty = rows.length === 0;

  const sourceIdx = resolveColumnIndex(spec.encoding.source.column, dimensions);
  const targetIdx = resolveColumnIndex(spec.encoding.target.column, dimensions);
  const valueIdx = resolveColumnIndex(spec.encoding.value.column, dimensions);

  const nodeNames = new Set<string>();
  // Aggregate links: (source, target) → summed value
  const linkMap = new Map<string, { source: string; target: string; value: number }>();

  if (sourceIdx !== null && targetIdx !== null && valueIdx !== null) {
    for (const row of rows) {
      const source = row[sourceIdx] == null ? "" : String(row[sourceIdx]);
      const target = row[targetIdx] == null ? "" : String(row[targetIdx]);
      const value = coerce(row[valueIdx]);
      if (!source || !target || value == null) continue;
      nodeNames.add(source);
      nodeNames.add(target);
      const key = `${source} ${target}`;
      const existing = linkMap.get(key);
      if (existing) existing.value += value;
      else linkMap.set(key, { source, target, value });
    }
  }

  const nodes = Array.from(nodeNames).map((name) => ({ name }));
  const links = Array.from(linkMap.values());

  const orient = spec.options.orient ?? "horizontal";
  const palette = resolvePalette(spec.options.color, nodes.length);
  const tooltipFormatter = buildTooltipFormatter(
    spec.options.tooltip,
    [{ name: spec.encoding.value.column }],
    spec.encoding.value.format,
  );

  return {
    ...(spec.options.title
      ? {
          title: {
            text: spec.options.title,
            ...(spec.options.subtitle ? { subtext: spec.options.subtitle } : {}),
          },
        }
      : {}),
    ...(palette ? { color: palette } : {}),
    legend: { show: spec.options.legend?.show !== false },
    tooltip: {
      show: spec.options.tooltip?.show !== false,
      trigger: "item",
      ...(tooltipFormatter ? { formatter: tooltipFormatter } : {}),
    },
    series: [
      {
        type: "sankey",
        orient,
        data: nodes,
        links,
      },
    ],
    ...(isEmpty ? { graphic: buildNoDataGraphic() } : {}),
  };
}

function coerce(v: unknown): number | null {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v === "string") {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}
